// Shared per-field Arabic/English merging for backend page content.
// English records are canonical: URLs, image sources, icons and layout keys
// always come from the English value so a translation can never replace a
// link or break a layout. Arabic text falls back per field when missing.

export type ContentLanguage = "en" | "ar";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const nonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

// Keys that identify canonical settings (links, media, icons, layout) and
// must never be overridden by translated text.
const CANONICAL_KEY_PATTERN = /(url|uri|link|href|src|image|icon|layout)/i;

/** Per-field fallback: Arabic when it is a non-empty string, else English. */
export function localizedText(en: unknown, ar: unknown, language: ContentLanguage): string {
  if (language === "ar" && nonEmptyString(ar)) return ar;
  return typeof en === "string" ? en : "";
}

/** Fill {placeholders} in a dictionary template with values. */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    values[key] !== undefined ? String(values[key]) : `{${key}}`,
  );
}

// Known gender values stored on collection products. Unknown values are
// returned unchanged (canonical English) so filtering/matching never breaks.
const GENDER_AR: Record<string, string> = {
  men: "رجالي",
  male: "رجالي",
  "for him": "رجالي",
  women: "نسائي",
  female: "نسائي",
  "for her": "نسائي",
  unisex: "للجنسين",
};

/** Localized display for a stored gender value; canonical value is the fallback. */
export function localizedGender(value: unknown, language: ContentLanguage): string {
  if (typeof value !== "string") return "";
  if (language === "ar") {
    const mapped = GENDER_AR[value.trim().toLowerCase()];
    if (mapped) return mapped;
  }
  return value;
}

/**
 * Merge a canonical English array with its Arabic counterpart by index.
 * Only the listed text keys are taken from Arabic; every other key (icon,
 * image_url, layout, links) stays canonical English. Items without an
 * English counterpart are ignored so ordering and media stay authoritative.
 */
export function mergeLocalizedArray<T = Record<string, unknown>>(
  enArr: unknown,
  arArr: unknown,
  language: ContentLanguage,
  textKeys: string[],
): T[] {
  const enList = Array.isArray(enArr) ? enArr : [];
  const arList = Array.isArray(arArr) ? arArr : [];
  if (enList.length === 0) {
    // No canonical items: in Arabic fall back to the Arabic list as-is.
    return (language === "ar" && arList.length > 0 ? arList : []) as T[];
  }
  return enList.map((enItem, index) => {
    const base: Record<string, unknown> = isPlainObject(enItem) ? { ...enItem } : {};
    if (language === "ar") {
      const arItem = arList[index];
      if (isPlainObject(arItem)) {
        for (const key of textKeys) {
          const value = arItem[key];
          if (nonEmptyString(value)) base[key] = value;
        }
      }
    }
    return base as T;
  });
}

/**
 * Merge a canonical English content object with its Arabic counterpart.
 * String fields fall back per field; canonical-looking keys (URLs, images,
 * icons, layouts) always stay English, and nested arrays/objects are left
 * to the caller (mergeLocalizedArray) so their canonical keys stay safe.
 */
export function mergeLocalizedContent(
  en: unknown,
  ar: unknown,
  language: ContentLanguage,
): Record<string, unknown> {
  const enObj = isPlainObject(en) ? en : {};
  if (language !== "ar" || !isPlainObject(ar)) return { ...enObj };
  const merged: Record<string, unknown> = { ...enObj };
  for (const [key, value] of Object.entries(ar)) {
    if (CANONICAL_KEY_PATTERN.test(key)) continue;
    if (nonEmptyString(value)) merged[key] = value;
  }
  return merged;
}
